import React, { useState, useEffect, useMemo } from "react";
import {
  Container,
  Typography,
  Grid,
  Paper,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TablePagination,
  TextField,
  MenuItem,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Menu,
  Tab,
  Tabs,
  FormHelperText,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  SelectChangeEvent,
  Chip,
  TableHead,
} from "@mui/material";
import {
  DirectionsBus,
  MoreVert,
  Close as CloseIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useToast } from "../components/Toast";
import axios from "axios";
import { Loading } from "../components/Loading";
import { getValidToken } from "../utils/authUtils";
import {
  formatCurrency,
  formatDate,
  formatVehicleNumber,
  TimeFilter,
  getDateRangeForFilter,
  formatDateRange,
  formatToLocalYMD,
} from "../utils/utils";
import { Edit as EditIcon, ContentCopy as CopyIcon } from "@mui/icons-material";
import { commonButtonProps, commonTextFieldProps } from "../utils/styles";
import * as yup from "yup";
import { useFormik } from "formik";
import { vehicleExpenseApi } from "./vehicleExpense/apiService";

import { usePlanStatus } from "../hooks/usePlanStatus";
import { Vehicle } from "../services/api";
import NavigationHeader from "../components/NavigationHeader";
import TimePeriodFilter from "../components/TimePeriodFilter";

import { TextFormatter } from "./vehicleExpense/components/PaymentDetailsFields";

import { PAYMENT_MODES } from "./otherExpense";

interface Trip {
  id: string;
  tripNumber: string;
  vehicleId: string;
  operatorId: string;
  source: string;
  destination: string;
  journeyStartDate: string;
  journeyReturnDate: string;
  status: string;
  totalRevenue: number | null;
  totalIncome: number;
  totalExpense: number;
  advancePayment: number;
  vehicle: {
    vehicleNumber: string;
    bodyType: string;
  };
  operator: {
    name: string;
  };
  cashAdvanceBalanceRecovered: boolean;
  advanceBalance: number;
  previousAdvanceBalance: number;
  totalCashAdvanceUtilized: number;
  balanceRecoveredAmount: number;
  advancePaymentPaidTo: string;
  advancePaymentLocation: string;
  advancePaymentMode: string;
  tripType: string;
  fromLocation: string;
  toLocation: string;
  previousTrip: {
    id: string;
    advanceBalance: number;
    advancePayment: number;
    balanceRecoveredAmount: number;
    balanceRecoveredMode: string;
  };
}

interface Pagination {
  total: number;
  page: number;
  limit: number;
  pages: number;
}

// interface Vehicle {
//   id: string;
//   vehicleNumber: string;
// }

const handleDeleteTrip = async (
  tripId: string,
  showToast: any,
  closeDeleteModal: any,
  onSuccess: () => void
) => {
  try {
    const token = await getValidToken();
    const url = `${process.env.REACT_APP_API_HOST}trips/${tripId}`;
    const response = await axios.delete(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    if (response.data?.isSuccess) {
      showToast("Trip deleted successfully", "success");
      closeDeleteModal();
      onSuccess();
    }
  } catch (error) {
    console.error("Error deleting trip:", error);
  }
};

const updateTripStatus = async (
  tripId: string,
  status: string,
  showToast: any,
  closeModal: any,
  onSuccess: () => void
) => {
  try {
    const token = await getValidToken();
    const url = `${process.env.REACT_APP_API_HOST}trips/${tripId}/status/${status}`;
    const response = await axios.patch(
      url,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    if (response.data?.isSuccess) {
      showToast("Trip completed successfully", "success");
      closeModal();
      onSuccess();
    }
  } catch (error) {
    console.error("Error closing trip:", error);
    showToast("Failed to close trip", "error");
    closeModal();
  }
};

const handleUpdateTrip = async (
  tripId: string,
  selectedTrip: Trip,
  showToast: any,
  closeModal: any,
  onSuccess: () => void,
  values: any
) => {
  try {
    const token = await getValidToken();
    const url = `${process.env.REACT_APP_API_HOST}trips/${tripId}`;
    const response = await axios.put(
      url,
      {
        tripType: selectedTrip.tripType,
        journeyStartDate: selectedTrip.journeyStartDate,
        ...(selectedTrip.tripType === "Round" && {
          journeyReturnDate: selectedTrip.journeyReturnDate,
        }),
        fromLocation: selectedTrip.fromLocation,
        toLocation: selectedTrip.toLocation,
        cashAdvanceBalanceRecovered: true,
        ...(values.balanceRecoveredAmount &&
          values.balanceRecoveredAmount > 0 && {
            balanceRecoveredAmount: values.balanceRecoveredAmount,
            balanceRecoveredFrom: values.balanceRecoveredFrom,
            balanceRecoveredDate: values.balanceRecoveredDate,
            balanceRecoveredLocation: values.balanceRecoveredLocation,
            balanceRecoveredMode: values.balanceRecoveredMode,
          }),
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    if (response.data?.isSuccess) {
      showToast("Trip updated successfully", "success");
      closeModal();
      onSuccess();
    }
  } catch (error) {
    console.error("Error updating trip:", error);
    showToast("Failed to update trip", "error");
    closeModal();
  }
};

const Trip = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [trips, setTrips] = useState<{ [key: string]: Trip[] }>({});
  const [filteredTrips, setFilteredTrips] = useState<Trip[]>([]);
  const [vehicleNumberFilter, setVehicleNumberFilter] = useState<string[]>([]);
  const [fromLocationFilter, setFromLocationFilter] = useState<string>("");
  const [toLocationFilter, setToLocationFilter] = useState<string>("");
  const [startDateFilter, setStartDateFilter] = useState<string>("");
  const [endDateFilter, setEndDateFilter] = useState<string>("");
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [tripIdToDelete, setTripIdToDelete] = useState<string | null>(null);
  const [closeModalOpen, setCloseModalOpen] = useState<boolean>(false);
  const [tripIdToClose, setTripIdToClose] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState<number>(0);
  const [pagination, setPagination] = useState<Pagination>({
    total: 0,
    page: 1,
    limit: 10,
    pages: 1,
  });
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedTripId, setSelectedTripId] = useState<string | null>(null);
  const [advanceRecoveredModalOpen, setAdvanceRecoveredModalOpen] =
    useState(false);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [selectedTrip, setSelectedTrip] = useState<Trip | null>(null);
  const [validationSchema] = useState(
    yup.object().shape({
      balanceRecoveredAmount: yup
        .number()
        .required("Balance recovered amount is required")
        .min(0, "Amount must be greater than or equal to 0"),
      balanceRecoveredFrom: yup
        .string()
        .required("Please specify from whom the balance was recovered"),
      balanceRecoveredDate: yup.string().required("Recovery date is required"),
      balanceRecoveredLocation: yup
        .string()
        .required("Recovery location is required"),
      balanceRecoveredMode: yup.string().required("Recovery mode is required"),
    })
  );

  const formik = useFormik({
    initialValues: {
      balanceRecoveredAmount: selectedTrip?.advanceBalance || 0,
      balanceRecoveredFrom: selectedTrip?.advancePaymentPaidTo || "",
      balanceRecoveredDate: new Date().toISOString().split("T")[0],
      balanceRecoveredLocation: selectedTrip?.advancePaymentLocation || "",
      balanceRecoveredMode: selectedTrip?.advancePaymentMode || "",
    },
    validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    enableReinitialize: true,
    onSubmit: async (values) => {
      if (
        values.balanceRecoveredAmount >
        Number(selectedTrip?.advanceBalance || 0)
      ) {
        formik.setFieldError(
          "balanceRecoveredAmount",
          "Amount cannot exceed remaining balance"
        );
        return;
      }
      try {
        await handleUpdateTrip(
          selectedTripId!,
          selectedTrip!,
          showToast,
          () => {
            setAdvanceRecoveredModalOpen(false);
            setSelectedTripId(null);
          },
          fetchTrips,
          values
        );
      } catch (error) {
        console.error("Error updating trip:", error);
        showToast("Failed to update trip", "error");
      }
    },
  });

  // Reset form when dialog opens
  useEffect(() => {
    if (advanceRecoveredModalOpen) {
      formik.resetForm({
        values: {
          balanceRecoveredAmount: selectedTrip?.advanceBalance || 0,
          balanceRecoveredFrom: selectedTrip?.advancePaymentPaidTo || "",
          balanceRecoveredDate: new Date().toISOString().split("T")[0],
          balanceRecoveredLocation: selectedTrip?.advancePaymentLocation || "",
          balanceRecoveredMode: selectedTrip?.advancePaymentMode || "",
        },
      });
    }
  }, [advanceRecoveredModalOpen, selectedTrip]);

  useEffect(() => {
    const fetchVehicles = async () => {
      try {
        const vehicleData = await vehicleExpenseApi.fetchVehicles();
        setVehicles(vehicleData);
      } catch (err) {
        console.error("Error fetching vehicles:", err);
      }
    };
    fetchVehicles();
  }, []);

  const [vehicleSearchText, setVehicleSearchText] = useState<string>("");
  const [timeFilter, setTimeFilter] = useState<TimeFilter | string>("");
  const [customStartDate, setCustomStartDate] = useState<string>("");
  const [customEndDate, setCustomEndDate] = useState<string>("");

  const filteredVehicles = useMemo(
    () =>
      vehicles.filter((vehicle) =>
        vehicle.vehicleNumber
          .toLowerCase()
          .includes(vehicleSearchText.toLowerCase())
      ),
    [vehicles, vehicleSearchText]
  );

  const handleVehicleChange = (event: SelectChangeEvent<string[]>) => {
    const { value } = event.target;
    setVehicleNumberFilter(
      typeof value === "string" ? value.split(",") : value
    );
  };

  const handleRemoveVehicle = (vehicleToRemove: string) => {
    setVehicleNumberFilter(
      vehicleNumberFilter.filter((vehicle) => vehicle !== vehicleToRemove)
    );
  };

  // Helper function to get current filter values
  const getCurrentFilters = () => {
    let startDate = startDateFilter;
    let endDate = endDateFilter;

    // If time filter is custom, use custom dates
    if (timeFilter === "custom" && customStartDate && customEndDate) {
      startDate = customStartDate;
      endDate = customEndDate;
    } else if (timeFilter === "today") {
      startDate = formatToLocalYMD(getDateRangeForFilter("today").start);
    } else if (timeFilter && timeFilter !== "custom") {
      // For non-custom filters, use the calculated date range
      const dateRange = getDateRangeForFilter(timeFilter as TimeFilter);
      startDate = formatToLocalYMD(dateRange.start);
      endDate = formatToLocalYMD(dateRange.end);
    }

    return {
      vehicleNumber: vehicleNumberFilter.join(","),
      fromLocation: fromLocationFilter,
      toLocation: toLocationFilter,
      startDate: startDate,
      endDate: endDate,
    };
  };

  const fetchTrips = async (
    page = pagination.page,
    limit = pagination.limit,
    filters = getCurrentFilters()
  ) => {
    try {
      setIsLoading(true);
      const token = await getValidToken();
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        status: tabValue === 1 ? "COMPLETED" : "PENDING",
      });

      // Add filter parameters to the query string if they exist
      if (filters.vehicleNumber) {
        params.append("vehicleNumber", filters.vehicleNumber);
      }
      if (filters.fromLocation) {
        params.append("fromLocation", filters.fromLocation);
      }
      if (filters.toLocation) {
        params.append("toLocation", filters.toLocation);
      }
      if (filters.startDate) {
        params.append("startDate", filters.startDate);
      }
      if (filters.endDate) {
        params.append("endDate", filters.endDate);
      }

      let url = `${process.env.REACT_APP_API_HOST}trips?${params.toString()}`;
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data?.data) {
        const { data, pagination: paginationData, normalData } = response.data;
        setTrips(data);
        setFilteredTrips(normalData);
        setPagination({
          total: paginationData.total,
          page: paginationData.page,
          limit: paginationData.limit,
          pages: paginationData.pages,
        });
      }
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTrips(1, 10, getCurrentFilters());
  }, [tabValue]);

  const handlePageChange = (event: unknown, newPage: number) => {
    fetchTrips(newPage + 1, pagination.limit, getCurrentFilters());
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newLimit = parseInt(event.target.value, 10);
    fetchTrips(1, newLimit, getCurrentFilters());
  };

  const tableCellStyles = {
    fontSize: "0.75rem",
    fontWeight: 400,
    textAlign: "center" as const,
    padding: "4px 8px",
    whiteSpace: "nowrap" as const,
  };

  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setTripIdToDelete(null);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const updateTimeFilter = (value: TimeFilter | string) => {
    setTimeFilter(value);
    // Clear custom dates when switching to non-custom filter
    if (value !== "custom") {
      setCustomStartDate("");
      setCustomEndDate("");
    }
  };

  const updateCustomDateRange = (startDate: string, endDate: string) => {
    setCustomStartDate(startDate);
    setCustomEndDate(endDate);
  };

  const { planStatus } = usePlanStatus();

  const { canCompleteTrip, canDeleteTrip, canRecoverAdvance } =
    React.useMemo(() => {
      return {
        canCompleteTrip: planStatus?.permissions?.canCompleteTrip || false,
        canDeleteTrip: planStatus?.permissions?.canDeleteTrip || false,
        canRecoverAdvance: planStatus?.permissions?.canRecoverAdvance || false,
      };
    }, [planStatus?.permissions]);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <NavigationHeader
        title="Trip Management"
        backNavigationEnabled={false}
        button={{
          label: "Create New Trip",
          onClick: () => navigate("/trip/daily-bus/basic-details"),
          icon: <DirectionsBus />,
        }}
      />

      {/* Filter */}
      <Paper sx={{ p: 1, mb: 1, backgroundColor: "background.paper" }}>
        <Grid
          container
          spacing={1}
          alignItems="center"
          justifyContent="space-between"
        >
          <Grid item xs={12} sm={6} md={timeFilter === "custom" ? 1.5 : 2}>
            <FormControl fullWidth size="small" sx={{ fontSize: "12px" }}>
              <InputLabel sx={{ fontSize: "12px" }}>Vehicles</InputLabel>
              <Select
                multiple
                value={vehicleNumberFilter}
                label="Vehicles"
                onChange={handleVehicleChange}
                sx={{ fontSize: "12px" }}
                input={<OutlinedInput label="Vehicles" />}
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {`${selected.length} vehicle${
                      selected.length !== 1 ? "s" : ""
                    } selected`}
                  </Box>
                )}
                onOpen={() => setVehicleSearchText("")}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 300,
                    },
                  },
                }}
              >
                <MenuItem dense sx={{ p: 0, borderBottom: "1px solid #eee" }}>
                  <TextField
                    size="small"
                    placeholder="Search vehicles number"
                    value={vehicleSearchText}
                    onChange={(e) => setVehicleSearchText(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    fullWidth
                    sx={{
                      "& .MuiOutlinedInput-notchedOutline": {
                        border: "none",
                      },
                    }}
                  />
                </MenuItem>
                {filteredVehicles.map((vehicle) => (
                  <MenuItem
                    key={vehicle.id}
                    value={vehicle.vehicleNumber}
                    sx={{
                      "&.Mui-selected": {
                        backgroundColor: "rgba(13, 76, 139, 0.12)",
                      },
                      "&.Mui-selected:hover": {
                        backgroundColor: "rgba(13, 76, 139, 0.12)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: "100%",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
                        {vehicle?.vehicleNumber}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: "0.55rem",
                          color: "text.secondary",
                          backgroundColor: "grey.100",
                          px: 1,
                          py: 0.25,
                          borderRadius: 0.5,
                        }}
                      >
                        {vehicle?.acquisitionType}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={timeFilter === "custom" ? 1.5 : 2}>
            <TextField
              fullWidth
              label="From Location"
              placeholder="Enter from location"
              value={fromLocationFilter}
              onChange={(e) => {
                setFromLocationFilter(e.target.value);
              }}
              {...commonTextFieldProps}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={timeFilter === "custom" ? 1.5 : 2}>
            <TextField
              fullWidth
              label="To Location"
              placeholder="Enter to location"
              value={toLocationFilter}
              onChange={(e) => {
                setToLocationFilter(e.target.value);
              }}
              {...commonTextFieldProps}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={timeFilter === "custom" ? 1.5 : 2}>
            <TimePeriodFilter
              timeFilter={timeFilter}
              onChange={(value) => updateTimeFilter(value)}
            />
          </Grid>

          {timeFilter === "custom" && (
            <>
              <Grid item xs={12} sm={6} md={1.5}>
                <TextField
                  fullWidth
                  type="date"
                  label="Start Date"
                  value={customStartDate}
                  onChange={(e) =>
                    updateCustomDateRange(e.target.value, customEndDate)
                  }
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ style: { fontSize: "12px" } }}
                  {...commonTextFieldProps}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={1.5}>
                <TextField
                  fullWidth
                  type="date"
                  label="End Date"
                  value={customEndDate}
                  onChange={(e) =>
                    updateCustomDateRange(customStartDate, e.target.value)
                  }
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ style: { fontSize: "12px" } }}
                  {...commonTextFieldProps}
                />
              </Grid>
            </>
          )}

          <Grid
            item
            xs={12}
            sm={6}
            md={timeFilter === "custom" ? 1.5 : 2}
            sx={{ display: "flex", gap: 1 }}
          >
            <Button
              variant="outlined"
              onClick={() => {
                fetchTrips(1, pagination.limit, getCurrentFilters());
              }}
              {...commonButtonProps}
              disabled={
                (vehicleNumberFilter.length === 0 &&
                  !fromLocationFilter &&
                  !toLocationFilter &&
                  !startDateFilter &&
                  !endDateFilter &&
                  timeFilter === "") ||
                (timeFilter === "custom" &&
                  (!customStartDate || !customEndDate))
              }
            >
              Apply
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                setVehicleNumberFilter([]);
                setFromLocationFilter("");
                setToLocationFilter("");
                setStartDateFilter("");
                setEndDateFilter("");
                setCustomStartDate("");
                setCustomEndDate("");
                setTimeFilter("");
                fetchTrips(1, pagination.limit, {
                  vehicleNumber: "",
                  fromLocation: "",
                  toLocation: "",
                  startDate: "",
                  endDate: "",
                });
              }}
              {...commonButtonProps}
              disabled={
                vehicleNumberFilter.length === 0 &&
                !fromLocationFilter &&
                !toLocationFilter &&
                !startDateFilter &&
                !endDateFilter &&
                !timeFilter &&
                (timeFilter !== "custom" || !customStartDate || !customEndDate)
              }
            >
              Clear
            </Button>
          </Grid>
          {vehicleNumberFilter.length > 0 && (
            <Grid item xs={12}>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
                {vehicleNumberFilter.map((vehicle) => (
                  <Chip
                    key={vehicle}
                    label={vehicle}
                    onDelete={() => {
                      handleRemoveVehicle(vehicle);
                    }}
                    deleteIcon={<CloseIcon />}
                    size="small"
                    sx={{ fontSize: "12px" }}
                  />
                ))}
              </Box>
            </Grid>
          )}
        </Grid>
      </Paper>

      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 1 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab
            label="Active Trips"
            sx={{ fontSize: "0.75rem", px: 1, py: 0.5 }}
          />
          <Tab
            label="Completed Trips"
            sx={{ fontSize: "0.75rem", px: 1, py: 0.5 }}
          />
        </Tabs>
      </Box>

      {/* Trips Table */}
      <Paper
        sx={{
          p: 1,
          backgroundColor: "background.paper",
          position: "relative",
          minHeight: "200px",
        }}
      >
        {isLoading ? (
          <Loading message="Loading trips..." fullScreen={false} />
        ) : (
          <>
            <TableContainer>
              <Table
                size="small"
                sx={{
                  "& .MuiTableCell-root": { py: 0.5, px: 0.5 },
                  "& .MuiTableCell-root:nth-of-type(1)": { width: "10%" }, // Vehicle
                  // "& .MuiTableCell-root:nth-of-type(2)": { width: "25%" }, // Route
                  // "& .MuiTableCell-root:nth-of-type(3)": { width: "15%" }, // Income
                  // "& .MuiTableCell-root:nth-of-type(4)": { width: "15%" }, // Expense
                  // "& .MuiTableCell-root:nth-of-type(5)": { width: "15%" }, // Profit/Loss
                  // "& .MuiTableCell-root:nth-of-type(6)": { width: "15%" }, // Cash Advance
                  // "& .MuiTableCell-root:nth-of-type(7)": { width: "5%" }, // Actions
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        ...tableCellStyles,
                        fontWeight: 600,
                        textAlign: "left",
                      }}
                    >
                      Vehicle
                    </TableCell>
                    <TableCell
                      sx={{
                        ...tableCellStyles,
                        fontWeight: 600,
                        textAlign: "left",
                      }}
                    >
                      Route
                    </TableCell>
                    <TableCell
                      sx={{
                        ...tableCellStyles,
                        fontWeight: 600,
                        textAlign: "left",
                      }}
                    >
                      Income
                    </TableCell>
                    <TableCell
                      sx={{
                        ...tableCellStyles,
                        fontWeight: 600,
                        textAlign: "left",
                      }}
                    >
                      Expense
                    </TableCell>
                    <TableCell
                      sx={{
                        ...tableCellStyles,
                        fontWeight: 600,
                        textAlign: "left",
                      }}
                    >
                      Cash Advance
                    </TableCell>
                    <TableCell
                      sx={{
                        ...tableCellStyles,
                        fontWeight: 600,
                        textAlign: "center",
                      }}
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(trips || {})?.length > 0 ? (
                    Object.entries(trips || {}).map(([date, trips]) => (
                      <React.Fragment key={date}>
                        {/* Date Header Row */}
                        <TableRow
                          sx={{
                            "& .MuiTableCell-root": {
                              borderBottomWidth: "0px",
                            },
                          }}
                        >
                          <TableCell
                            colSpan={6}
                            sx={{
                              py: 1,
                              px: 2,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                color: "text.primary",
                                fontSize: "0.75rem",
                                fontWeight: 600,
                              }}
                            >
                              {formatDate(date)}
                            </Typography>
                          </TableCell>
                        </TableRow>

                        {/* Trip Rows for this date */}
                        {trips?.map((trip: Trip) => (
                          <TableRow key={trip?.id} hover>
                            <TableCell sx={{ ...tableCellStyles }}>
                              <Typography
                                variant="caption"
                                sx={{
                                  fontSize: "0.75rem",
                                  fontWeight: 500,
                                }}
                              >
                                {formatVehicleNumber(
                                  trip?.vehicle?.vehicleNumber
                                )}
                              </Typography>
                            </TableCell>
                            <TableCell
                              sx={{
                                ...tableCellStyles,
                                textAlign: "left",
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{ fontSize: "0.75rem" }}
                              >
                                {trip?.fromLocation} → {trip?.toLocation} - (
                                {trip.tripType})
                              </Typography>
                            </TableCell>
                            <TableCell
                              sx={{
                                ...tableCellStyles,
                                textAlign: "left",
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{ fontSize: "0.75rem" }}
                              >
                                {formatCurrency(trip?.totalIncome || 0)}
                              </Typography>
                            </TableCell>
                            <TableCell
                              sx={{
                                ...tableCellStyles,
                                textAlign: "left",
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{ fontSize: "0.75rem" }}
                              >
                                {formatCurrency(trip?.totalExpense || 0)}
                              </Typography>
                            </TableCell>
                            <TableCell
                              sx={{
                                ...tableCellStyles,
                                textAlign: "left",
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{ fontSize: "0.75rem" }}
                              >
                                {formatCurrency(
                                  Number(trip?.advancePayment || 0) +
                                    Number(
                                      trip?.previousTrip?.advanceBalance || 0
                                    ) -
                                    Number(
                                      trip?.previousTrip
                                        ?.balanceRecoveredAmount || 0
                                    )
                                ) + " (advance)"}
                                {" | "}
                                {formatCurrency(
                                  Number(trip?.advanceBalance || 0)
                                ) + " (balance)"}

                                {" | "}
                                {formatCurrency(
                                  Number(trip?.balanceRecoveredAmount || 0)
                                ) + " (recovered)"}
                              </Typography>
                            </TableCell>
                            <TableCell
                              sx={{
                                ...tableCellStyles,
                                textAlign: "center",
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  gap: 0.25,
                                  justifyContent: "center",
                                  alignItems: "center",
                                }}
                              >
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={() =>
                                    navigate(
                                      `/trip/daily-bus/${trip?.id}/bus-income-details`
                                    )
                                  }
                                  title="Edit Trip"
                                  sx={{
                                    py: 0.25,
                                    px: 0.5,
                                    minWidth: "auto",
                                    fontSize: "0.75rem",
                                  }}
                                >
                                  Edit
                                </Button>
                                {(canDeleteTrip ||
                                  canRecoverAdvance ||
                                  canCompleteTrip ||
                                  (trip.totalIncome === 0 &&
                                    trip.totalExpense === 0)) && (
                                  <IconButton
                                    size="small"
                                    onClick={(event) => {
                                      setAnchorEl(event.currentTarget);
                                      setSelectedTripId(trip?.id);
                                      setSelectedTrip(trip);
                                    }}
                                    title="More Options"
                                    sx={{
                                      color: "primary.main",
                                      minWidth: "auto",
                                      "&:hover": {
                                        backgroundColor: "action.hover",
                                      },
                                      "& .MuiSvgIcon-root": {
                                        fontSize: "1rem",
                                      },
                                    }}
                                  >
                                    <MoreVert />
                                  </IconButton>
                                )}
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </React.Fragment>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography
                          variant="h6"
                          color="text.secondary"
                          gutterBottom
                        >
                          {vehicleNumberFilter.length > 0 ||
                          fromLocationFilter ||
                          toLocationFilter ||
                          startDateFilter ||
                          endDateFilter
                            ? "No trips found with your selected filters"
                            : `No ${
                                tabValue === 0 ? "active" : "completed"
                              } trips available`}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {vehicleNumberFilter.length > 0 ||
                          fromLocationFilter ||
                          toLocationFilter ||
                          startDateFilter ||
                          endDateFilter
                            ? "Try changing or removing the filters to see more trip options"
                            : tabValue === 0
                            ? "Create a new trip to get started"
                            : "Close some trips to see them here"}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={pagination.total}
              page={pagination.page - 1}
              onPageChange={handlePageChange}
              rowsPerPage={pagination.limit}
              onRowsPerPageChange={handleRowsPerPageChange}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </>
        )}
      </Paper>

      {/* Trip Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => {
          setAnchorEl(null);
          setSelectedTripId(null);
        }}
      >
        {tabValue === 0 && canCompleteTrip && (
          <>
            <MenuItem
              sx={{ fontSize: "0.75rem" }}
              onClick={() => {
                setTripIdToClose(selectedTripId);
                setCloseModalOpen(true);
                setAnchorEl(null);
              }}
            >
              Complete Trip
            </MenuItem>
          </>
        )}
        {/* Show Advance Recovered option only when advance payment exists and is not fully utilized */}
        {(() => {
          if (!canRecoverAdvance || !selectedTripId) return null;

          const currentTrip = filteredTrips?.find(
            (t) => t.id === selectedTripId
          );
          const totalAdvancePayment =
            Number(currentTrip?.advancePayment || 0) +
            Number(currentTrip?.previousTrip?.advanceBalance || 0) -
            Number(currentTrip?.previousTrip?.balanceRecoveredAmount || 0);

          const totalCashAdvanceUtilized = Number(
            currentTrip?.advanceBalance || 0
          );
          if (!currentTrip) return null;

          const isNotRecovered = !currentTrip?.cashAdvanceBalanceRecovered;

          return totalCashAdvanceUtilized > 0 && isNotRecovered ? (
            <MenuItem
              sx={{ fontSize: "0.75rem" }}
              onClick={() => {
                setAdvanceRecoveredModalOpen(true);
                setAnchorEl(null);
              }}
            >
              Advance Recovered
            </MenuItem>
          ) : null;
        })()}
        {(canDeleteTrip ||
          (selectedTrip?.totalIncome === 0 &&
            selectedTrip?.totalExpense === 0)) && (
          <MenuItem
            onClick={() => {
              setTripIdToDelete(selectedTripId);
              setDeleteModalOpen(true);
              setAnchorEl(null);
            }}
            sx={{ color: "error.main", fontSize: "0.75rem" }}
          >
            Delete Trip
          </MenuItem>
        )}
      </Menu>

      {/* Delete Trip Dialog */}
      <Dialog
        open={deleteModalOpen}
        onClose={closeDeleteModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: "error.main", fontWeight: 600 }}>
          Delete Trip
        </DialogTitle>
        <DialogContent sx={{ pb: 3, pt: 2 }}>
          <Typography variant="body2" sx={{ mb: 3 }}>
            Are you sure you want to delete this trip?
          </Typography>
          <Box display="flex" justifyContent="space-between" gap={2} mt={2}>
            <Button
              variant="outlined"
              color="inherit"
              onClick={closeDeleteModal}
              sx={{ px: 2, py: 0.5, fontSize: "0.8rem" }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={() =>
                handleDeleteTrip(
                  tripIdToDelete!,
                  showToast,
                  closeDeleteModal,
                  fetchTrips
                )
              }
              sx={{ px: 2, py: 0.5, fontSize: "0.8rem" }}
            >
              Delete
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
      {/* Complete Trip Dialog */}
      <Dialog
        open={closeModalOpen}
        onClose={() => {
          setCloseModalOpen(false);
          setTripIdToClose(null);
        }}
        maxWidth="sm"
      >
        <DialogTitle sx={{ color: "primary.main", fontWeight: 600 }}>
          Complete Trip
        </DialogTitle>
        <DialogContent sx={{ pb: 3, pt: 2 }}>
          <Typography variant="body2" sx={{ mb: 3 }}>
            Are you sure you want to complete this trip?
          </Typography>
          <Box display="flex" justifyContent="space-between" gap={2} mt={2}>
            <Button
              variant="outlined"
              color="inherit"
              onClick={() => {
                setCloseModalOpen(false);
                setTripIdToClose(null);
              }}
              sx={{ px: 2, py: 0.5, fontSize: "0.8rem" }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() =>
                updateTripStatus(
                  tripIdToClose!,
                  "COMPLETED",
                  showToast,
                  () => {
                    setCloseModalOpen(false);
                    setTripIdToClose(null);
                  },
                  fetchTrips
                )
              }
              sx={{ px: 2, py: 0.5, fontSize: "0.8rem" }}
            >
              Complete
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
      {/* Advance Recovered Dialog */}
      <Dialog
        open={advanceRecoveredModalOpen}
        onClose={() => {
          setAdvanceRecoveredModalOpen(false);
          setSelectedTripId(null);
          formik.resetForm();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{ color: "primary.main", fontWeight: 600, fontSize: "0.75rem" }}
        >
          Advance Recovered
        </DialogTitle>
        <DialogContent sx={{ pb: 3, pt: 2 }}>
          <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  gutterBottom
                  sx={{ fontSize: "0.75rem" }}
                >
                  Total Advance Payment
                </Typography>
                <Typography variant="h6" sx={{ mb: 2, fontSize: "0.75rem" }}>
                  {formatCurrency(
                    Number(selectedTrip?.advancePayment || 0) +
                      Number(selectedTrip?.previousTrip?.advanceBalance || 0) -
                      Number(
                        selectedTrip?.previousTrip?.balanceRecoveredAmount || 0
                      )
                  )}
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  gutterBottom
                  sx={{ fontSize: "0.75rem" }}
                >
                  Total Cash Advance Balance
                </Typography>
                <Typography variant="h6" sx={{ mb: 2, fontSize: "0.75rem" }}>
                  {formatCurrency(Number(selectedTrip?.advanceBalance || 0))}
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  gutterBottom
                  sx={{ fontSize: "0.75rem" }}
                >
                  Balance Recovered Amount
                </Typography>
                <TextField
                  fullWidth
                  type="number"
                  name="balanceRecoveredAmount"
                  value={formik.values.balanceRecoveredAmount}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.balanceRecoveredAmount &&
                    Boolean(formik.errors.balanceRecoveredAmount)
                  }
                  helperText={
                    formik.touched.balanceRecoveredAmount &&
                    formik.errors.balanceRecoveredAmount
                  }
                  inputProps={{
                    min: 0,
                    max: selectedTrip?.advanceBalance || 0,
                    step: "0.01",
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">₹</InputAdornment>
                    ),
                  }}
                  {...commonTextFieldProps}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  gutterBottom
                  sx={{ fontSize: "0.75rem" }}
                >
                  Recovered From
                </Typography>
                <TextField
                  fullWidth
                  name="balanceRecoveredFrom"
                  value={formik.values.balanceRecoveredFrom}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.balanceRecoveredFrom &&
                    Boolean(formik.errors.balanceRecoveredFrom)
                  }
                  helperText={
                    formik.touched.balanceRecoveredFrom &&
                    formik.errors.balanceRecoveredFrom
                  }
                  {...commonTextFieldProps}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  gutterBottom
                  sx={{ fontSize: "0.75rem" }}
                >
                  Recovery Date
                </Typography>
                <TextField
                  fullWidth
                  type="date"
                  name="balanceRecoveredDate"
                  value={formik.values.balanceRecoveredDate}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.balanceRecoveredDate &&
                    Boolean(formik.errors.balanceRecoveredDate)
                  }
                  helperText={
                    formik.touched.balanceRecoveredDate &&
                    formik.errors.balanceRecoveredDate
                  }
                  {...commonTextFieldProps}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  gutterBottom
                  sx={{ fontSize: "0.75rem" }}
                >
                  Recovery Mode
                </Typography>
                <TextField
                  fullWidth
                  type="select"
                  select
                  name="balanceRecoveredMode"
                  value={formik.values.balanceRecoveredMode}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.balanceRecoveredMode &&
                    Boolean(formik.errors.balanceRecoveredMode)
                  }
                  helperText={
                    formik.touched.balanceRecoveredMode &&
                    formik.errors.balanceRecoveredMode
                  }
                  {...commonTextFieldProps}
                >
                  {PAYMENT_MODES.map((mode) => (
                    <MenuItem
                      key={mode}
                      value={mode}
                      sx={{ fontSize: "0.75rem" }}
                    >
                      {TextFormatter[mode as keyof typeof TextFormatter]}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} md={4}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  gutterBottom
                  sx={{ fontSize: "0.75rem" }}
                >
                  Recovery Location
                </Typography>
                <TextField
                  fullWidth
                  name="balanceRecoveredLocation"
                  value={formik.values.balanceRecoveredLocation}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.balanceRecoveredLocation &&
                    Boolean(formik.errors.balanceRecoveredLocation)
                  }
                  helperText={
                    formik.touched.balanceRecoveredLocation &&
                    formik.errors.balanceRecoveredLocation
                  }
                  {...commonTextFieldProps}
                />
              </Grid>
            </Grid>

            <Box display="flex" justifyContent="space-between" gap={2} mt={2}>
              <Button
                variant="outlined"
                color="inherit"
                onClick={() => {
                  setAdvanceRecoveredModalOpen(false);
                  setSelectedTripId(null);
                  formik.resetForm();
                }}
                sx={{ px: 2, py: 0.5, fontSize: "0.8rem" }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                disabled={!formik.isValid || formik.isSubmitting}
                sx={{ px: 2, py: 0.5, fontSize: "0.8rem" }}
              >
                {formik.isSubmitting ? "Submitting..." : "Mark as Recovered"}
              </Button>
            </Box>
          </form>
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default Trip;
